import { useState, useEffect } from "react";
import { FundCalendar } from "@/types";
import { getFundCalendars, getFundCalendarsByFundId } from "@/api/FundCalendar";
import { useAtom } from "jotai";
import {
  ClientStateAtom,
  FundCalendarStateAtom,
  SelectedFundCalendarAtom,
} from "@/store";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { useLocation } from "react-router-dom";

export const useFundCalendarsController = () => {
  const [fundCalendars, setFundCalendars] = useAtom(FundCalendarStateAtom);
  const [selectedCalendar, setSelectedCalendar] = useAtom(
    SelectedFundCalendarAtom
  );
  const [selectedClient] = useAtom(ClientStateAtom);
  const [selectedFund] = useAtom(SelectedFundAtom);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewContext, setViewContext] = useState<"client" | "fund">("client");

  const location = useLocation();

  useEffect(() => {
    // Parse context from URL query parameters
    const params = new URLSearchParams(location.search);
    const context = params.get("context");
    if (context === "fund") {
      setViewContext("fund");
    } else {
      setViewContext("client");
    }
  }, [location.search]);

  const fetchCalendars = async () => {
    setIsLoading(true);

    let calendars: FundCalendar[] = [];

    // Get calendars based on context
    if (viewContext === "fund" && selectedFund) {
      // Get calendars specific to this fund
      calendars = await getFundCalendarsByFundId(selectedFund.fundId);
    } else {
      // Get all calendars
      calendars = await getFundCalendars();

      // Filter by client if needed
      if (selectedClient) {
        calendars = calendars.filter(
          (calendar) => calendar.clientId === selectedClient.clientId
        );
      }
    }

    setFundCalendars(calendars);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCalendars();
  }, [selectedClient, selectedFund, viewContext, setFundCalendars]);

  const handleCalendarSelect = (calendar: FundCalendar) => {
    setSelectedCalendar(calendar);
  };

  const handleCreateNew = () => {
    setSelectedCalendar(null);
    setIsModalOpen(true);
  };

  const handleEditCalendar = (calendar: FundCalendar) => {
    setSelectedCalendar(calendar);
    setIsModalOpen(true);
  };

  const handleModalClose = (shouldRefresh: boolean = false) => {
    setIsModalOpen(false);
    if (shouldRefresh) {
      fetchCalendars();
    }
  };

  return {
    fundCalendars,
    selectedCalendar,
    isModalOpen,
    isLoading,
    viewContext,
    handleCalendarSelect,
    handleCreateNew,
    handleEditCalendar,
    handleModalClose,
    fetchCalendars,
  };
};
